import {
  dripMediumOptions,
  dripMediumOptionsWithEmail,
  dripMediumOptionsWithText
} from 'features/CustomCampaigns/EditCampaign/CampaignSetup'

const REQUIRED_SEQUENCE_TAGS = [
  '@[hotelName](__hotelName__)',
  '@[guestName](__guestName__)'
]

export const DRIP_SEQUENCE_STEP_IDS = {
  step1: 'step1',
  step2: 'step2',
  step3: 'step3'
}

const DEFAULT_MESSAGE_TEMPLATE =
  'Hi @[guestName](__guestName__), we would love to welcome you back to @[hotelName](__hotelName__)! Book your next stay with us.'

const DEFAULT_FOLLOW_UP_1_TEMPLATE =
  'Hi @[guestName](__guestName__), just a friendly reminder from @[hotelName](__hotelName__) — we have a special offer waiting for you.'

const DEFAULT_FOLLOW_UP_2_TEMPLATE =
  'Hi @[guestName](__guestName__), this is our final reminder from @[hotelName](__hotelName__). We hope to see you soon!'

export function convertToHours(value, unit) {
  return unit === 'days' ? value * 24 : value
}

export function convertFromHours(hours) {
  if (!hours) return { value: 2, unit: 'days', displayValue: 2 }
  if (hours % 24 === 0) {
    return { value: hours, unit: 'days', displayValue: hours / 24 }
  }
  return { value: hours, unit: 'hours', displayValue: hours }
}

export function getConditionLabel(hours) {
  const { unit, displayValue } = convertFromHours(hours)
  const singularUnit = unit === 'days' ? 'day' : 'hour'
  return {
    value: displayValue,
    unit: displayValue === 1 ? singularUnit : unit
  }
}

export function createDefaultSequenceSteps(campaignData = {}) {
  const medium = campaignData.medium || dripMediumOptions.EmailAndText
  const hasEmail = dripMediumOptionsWithEmail.includes(medium)
  const messageTemplate = campaignData.messageTemplate || DEFAULT_MESSAGE_TEMPLATE
  const messageMedia = campaignData.messageMediaUrls?.[0] ?? null

  return [
    {
      id: DRIP_SEQUENCE_STEP_IDS.step1,
      title: 'Initial message',
      isActive: true,
      hoursAfterPreviousCampaign: 0,
      medium,
      messageTemplate,
      messageMedia,
      hasEmailCampaign: hasEmail,
      isInitialStep: true
    },
    {
      id: DRIP_SEQUENCE_STEP_IDS.step2,
      title: 'Follow-up 1',
      isActive: true,
      hoursAfterPreviousCampaign: 48,
      medium,
      messageTemplate: DEFAULT_FOLLOW_UP_1_TEMPLATE,
      messageMedia: null,
      hasEmailCampaign: hasEmail,
      isInitialStep: false
    },
    {
      id: DRIP_SEQUENCE_STEP_IDS.step3,
      title: 'Follow-up 2',
      isActive: false,
      hoursAfterPreviousCampaign: 72,
      medium,
      messageTemplate: DEFAULT_FOLLOW_UP_2_TEMPLATE,
      messageMedia: null,
      hasEmailCampaign: hasEmail,
      isInitialStep: false
    }
  ]
}

export function normalizeSequenceSteps(steps, campaignData) {
  if (!Array.isArray(steps) || steps.length === 0) {
    return createDefaultSequenceSteps(campaignData)
  }

  return steps.map(step => ({
    ...step,
    messageMedia: step.messageMedia ?? step.messageMediaUrls?.[0] ?? null
  }))
}

export function replaceSequenceTags(text, { userName, hotelName, isDemoMode }) {
  if (!text) return ''

  const firstName = userName?.substring(0, userName?.indexOf(' ')) || 'Guest'
  const lastName = userName?.split(' ').pop() || ''
  const displayHotelName = isDemoMode ? 'The Luxury Resort & Spa' : hotelName

  return text
    .replace(/@\[(\S+)\]\((__)?(\S+?)(__)?\)/gm, '$1')
    .replace(/guestLastName/g, lastName)
    .replace(/guestName/g, firstName)
    .replace(/hotelName/g, displayHotelName)
    .replace(/promoName/g, 'Special Offer')
}

export function getStepDialogTitle(stepId) {
  switch (stepId) {
    case DRIP_SEQUENCE_STEP_IDS.step1:
      return 'Initial message'
    case DRIP_SEQUENCE_STEP_IDS.step2:
      return 'Follow-up 1'
    case DRIP_SEQUENCE_STEP_IDS.step3:
      return 'Follow-up 2'
    default:
      return 'Edit step'
  }
}

export function isFollowUpStepDisabled(steps, stepId) {
  if (stepId === DRIP_SEQUENCE_STEP_IDS.step3) {
    const followUp1 = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step2)
    return !followUp1?.isActive
  }
  return false
}

export function syncPrimaryStepToCampaign(steps) {
  const initialStep = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step1)
  if (!initialStep) return {}

  return {
    messageTemplate: initialStep.messageTemplate,
    messageMediaUrls: initialStep.messageMedia ? [initialStep.messageMedia] : [],
    medium: initialStep.medium
  }
}

export function applyMediumToSteps(steps, medium) {
  if (!Array.isArray(steps)) return steps

  const hasEmail = dripMediumOptionsWithEmail.includes(medium)

  return steps.map(step => ({
    ...step,
    medium,
    hasEmailCampaign: hasEmail
  }))
}

export function validateSequenceSteps(steps, medium) {
  const errors = {}

  if (!Array.isArray(steps) || steps.length === 0) {
    return { errors, isValid: false }
  }

  const includesText = dripMediumOptionsWithText.includes(medium)

  steps.forEach(step => {
    if (!step.isActive) return

    if (step.id === DRIP_SEQUENCE_STEP_IDS.step3) {
      const followUp1 = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step2)
      if (!followUp1?.isActive) return
    }

    if (includesText) {
      const template = step.messageTemplate || ''

      if (!template.trim()) {
        errors[step.id] = 'Add a text message for this step.'
        return
      }

      const missingTag = REQUIRED_SEQUENCE_TAGS.some(tag => !template.includes(tag))
      if (missingTag) {
        errors[step.id] =
          'Include the guest name and hotel name tags in the text message.'
      }
    }
  })

  const initialStep = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step1)
  if (!initialStep?.isActive) {
    errors[DRIP_SEQUENCE_STEP_IDS.step1] = 'The initial message must be active.'
  }

  return { errors, isValid: Object.keys(errors).length === 0 }
}
